import { useMemo, useCallback, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from "react-query";
import i18n from "i18next";
import { add, differenceInSeconds } from 'date-fns';
// import cn from 'classnames';
import ConfirmModal from "@components/modals/ConfirmModal";
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import { Button, ButtonSize, ButtonType } from "@components/new/Button";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import DataTable from '@containers/DataTableNew';
import { Filters, FilterTypes } from '@containers/FiltersNew';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import {
  cancelFBPWithdrawalRequest,
  getFBPWithdrawalRequests,
  TWithdrawalRequestsPageItem,
  TWarehouse,
  TRequestParams,
} from "@api/apiClient/fbp";
import ButtonWithTimer from '@components/new/ButtonWithTimer/ButtonWithTimer';
import { TableColumn } from "@components/new/Table";
import WithdrawalModal from "../modals/WithdrawalModal";
import CreateWithdrawalModal from "../modals/CreateWithdrawalModal";
import { weightGValue, formatDate, formatNumber } from "@utils/formatValues";
import WithdrwalStatusBadge from "../components/WithdrwalStatusBadge";
import ToDisposal from "../components/ToDisposal";
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import InfoIcon from '@assets/icons/outline/info.svg?react';
import IconPlus from '@assets/icons/outline/plus.svg?react';
import styles from './Tab.module.scss';

const OrdersTabWithdrawal = ({
  sellerWarehouseId,
  // activeWarehouse,
}: {
  sellerWarehouseId: string;
  activeWarehouse: TWarehouse | undefined;
}): ReactElement => {
  const { t } = useTranslation();
  const currentLanguage = i18n.language;
  const queryClient = useQueryClient();
  const [isOpenOrderModal, setOpenOrderModal] = useState<boolean>(false);
  const [isOpenWithdrawalModal, setOpenWithdrawalModal] = useState<boolean>(false);
  const [currentRequestId, setCurrentRequestId] = useState<string>("");
  const [sorting, setSorting] = useState<{ column: string, direction: string } | undefined>(undefined);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const { setKnowledgeBaseParams } = useKnowledgeBase();
  const [isOpenCancelWindow, setIsOpenCancelWindow] = useState<boolean>(false);
  const [cancelRequest, setCancelRequest] = useState<TWithdrawalRequestsPageItem | undefined>(undefined);

  const cancelRequestMutation = useMutation({
    mutationFn: (id: string) => cancelFBPWithdrawalRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries(
        { queryKey: ["getFBPWithdrawalRequests"] },
      );
      queryClient.invalidateQueries(
        { queryKey: ["getWarehouses"] },
      );
    },
  });

  const columns: TableColumn<TWithdrawalRequestsPageItem>[] = useMemo(() => [
    {
      header: t("fbpPage.ordersTab.columns.number"),
      renderCell: (row) => (
        <CopyTextToClipboard
          text={row.data?.number}
          visibleOnHover
        >
          {row.data?.number}
        </CopyTextToClipboard>
      ),
      width: "160px",
      // fixWidth: true,
    },
    {
      id: "count",
      header: t("fbpPage.ordersTab.columns.countShort"),
      valueGetter: (data: TWithdrawalRequestsPageItem) => (data.ymProductsCount + data.wbProductsCount + data.aliProductsCount),
      // alignment: "center",
      width: "160px",
      // fixWidth: true,
      // sortable: true,
    },
    {
      id: "weight",
      header: t("fbpPage.ordersTab.columns.weight"),
      valueGetter: (data: TWithdrawalRequestsPageItem) => {
        const value = (weightGValue(data.wbTotalWeight) + weightGValue(data.ymTotalWeight) + weightGValue(data.aliTotalWeight)) / 1000;
        return value ? formatNumber(value) : "0";
      },
      // alignment: "center",
      width: "160px",
      // fixWidth: true,
      // sortable: true,
    },
    {
      id: "createdAt",
      header: t("fbpPage.ordersTab.columns.createdAt"),
      valueGetter: (data: TWithdrawalRequestsPageItem) => formatDate(data.createdAt, 'dd.MM.yyyy'),
      // alignment: "center",
      width: "160px",
      // fixWidth: true,
      // sortable: true,
    },
    {
      header: t("fbpPage.ordersTab.columns.status"),
      renderCell: (row) => (<WithdrwalStatusBadge value={row.data.status} />),
      // alignment: "center",
      width: "160px",
      // fixWidth: true,
    },
    {
      header: t("fbpPage.ordersTab.columns.daysToUtilization"),
      renderCell: (row) => {
        if (row.data.isCancelable) {
          const endDate = add(row.data.createdAt, { hours: 1 });
          const timeout = differenceInSeconds(new Date(endDate), new Date());

          if (timeout > 0) {
            return (
              <ButtonWithTimer
                key={`${row.data.id}-${row.data.createdAt}`}
                type={ButtonType.SECONDARY}
                onClick={() => {
                  setIsOpenCancelWindow(true);
                  setCancelRequest(row.data);
                }}
                pending={cancelRequestMutation.isLoading}
                title={t("fbpPage.ordersTab.cancelBtn")}
                timeout={timeout > 0 ? timeout : 0}
                startTimerFromOutside
                isWide
              />
            );
          }
        }
        return (<ToDisposal
          disposalDate={row.data.disposalDate}
          status={row.data.status}
        />);
      },
      alignment: "center",
      width: "160px",
      // fixWidth: true,
    },
  ], [currentLanguage]);

  const ordersGetter = useCallback(
    ({ filters, page }: TRequestParams) => getFBPWithdrawalRequests({
      filters,
      sorting: {
        column: "Status",
        direction: "ASC",
      },
      page,
      sellerWarehouseId,
    }),
    [sellerWarehouseId]
  );

  return (
    <div className={styles.tabContent}>
      <div className={styles.actionsRow}>
        <Filters
          id="fbp-orders"
          // openFilters={() => setIsFilterOpen(true)}
          items={[
            {
              name: 'numbers',
              type: FilterTypes.MULTI_SEARCH_NEW,
              placeholder: t('fbpPage.requestSearch'),
            },
          ]}
          onChange={(v) => {
            setInnerFilters(v);
          }}
          values={innerFilters}
          // asPanel
        />

        <div className={styles.actionsRight}>
          <LinkButton
            icon={InfoIcon}
            size={LinkButtonSize.L}
            title={t('fbpPage.instructions')}
            onClick={() => setKnowledgeBaseParams(`https://unitrade-global.com/page139704916.html`)}
          />
          <Button
            size={ButtonSize.M}
            onClick={() => setOpenWithdrawalModal(true)}
            title={t('fbpPage.addOrderBtn')}
            icon={IconPlus}
          />
        </div>
      </div>

      <DataTable<TWithdrawalRequestsPageItem>
        key={sellerWarehouseId}
        columns={columns}
        filters={innerFilters}
        queryKey={["getFBPWithdrawalRequests", sellerWarehouseId]}
        getData={ordersGetter}
        rowOnClick={(row) => {
          setCurrentRequestId(row.id)
          setOpenOrderModal(true);
        }}
        verticalAlignment="top"
        noLoaderOnRefresh
        noDataProps={{
          title: t("fbpPage.noWithdrawalsTitle"),
          btnTitle: t('fbpPage.addWithdrawalBtn'),
          onClick: () => setOpenWithdrawalModal(true),
          width: "max-content",
        }}
        pending={!sellerWarehouseId}
        sorting={sorting}
        onSortingChange={(column, direction) => setSorting({ column, direction })}
      />
      {isOpenOrderModal ? (
        <WithdrawalModal
          onClose={() => setOpenOrderModal(false)}
          id={currentRequestId}
        />
      ) : null}

      {isOpenWithdrawalModal ? (
        <CreateWithdrawalModal
          onClose={() => setOpenWithdrawalModal(false)}
          sellerWarehouseId={sellerWarehouseId}
          // fbpWarehouseId={activeWarehouse?.id ?? ""}
        />
      ) : null}

      {isOpenCancelWindow && (
        <ConfirmModal
          key={cancelRequest?.id}
          title={t("fbpPage.cancelRequestModal.title")}
          description={t("fbpPage.cancelRequestModal.description", { number: cancelRequest?.number })}
          noBtnTitle={t("fbpPage.cancelRequestModal.cancelBtn")}
          yesBtnTitle={t("fbpPage.cancelRequestModal.okBtn")}
          onClose={() => setIsOpenCancelWindow(false)}
          requestFn={() => cancelRequestMutation.mutate(cancelRequest?.id ?? "")}
          onSuccess={() => setIsOpenCancelWindow(false)}
        />
      )}
    </div>
  );
};

export default OrdersTabWithdrawal;
